import React, { useState } from 'react';
import * as XLSX from 'xlsx';
import { Category, WorkOrder } from '@twinsketch/topika-model';
import FileService from 'src/services/FileService';

const ExcelFileUploader = ({
  workOrderId,
  isWorkOrderCreated,
  workOrderData,
  setWorkOrderData,
  setError,
}: {
  workOrderId: string;
  isWorkOrderCreated: boolean;
  workOrderData: WorkOrder;
  setWorkOrderData: (data: any) => void;
  setError: (error: string | null) => void;
}) => {
  const [file, setFile] = useState<File | null>(null);
  const [isFileSelected, setIsFileSelected] = useState(false);
  const [notes, setNotes] = useState<string>('');
  const [fileCategory, setFileCategory] = useState<Category | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [uploadComplete, setUploadComplete] = useState(false);
  const [sheetNames, setSheetNames] = useState<string[]>([]);

  const handleCategoryChange = (selectedCategory: Category) => {
    setFileCategory(selectedCategory);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files ? e.target.files[0] : null;
    if (
      selectedFile &&
      selectedFile.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    ) {
      setFile(selectedFile);
      setIsFileSelected(true);
      setUploadComplete(false);
    } else {
      alert('Please select a valid Excel file.');
    }
  };

  const handleUpload = async () => {
    if (!file || !fileCategory) {
      const message = 'Please select a file and category.';
      setUploadError(message);
      alert(message);
      return;
    }

    try {
      // Read the file to extract sheet names
      const arrayBuffer = await file.arrayBuffer();
      const workbook = XLSX.read(arrayBuffer, { type: 'array' });
      const validSheetNames = workbook.SheetNames; // or use your filtering logic

      // Optionally filter using keywords: will update this
      // const filteredSheetNames = workbook.SheetNames.filter(name => ...);
      setSheetNames(validSheetNames);

      const uploadedFileRecord = await FileService.uploadFileWithPresignedUrl(
        file,
        'WorkOrder',
        workOrderId,
        notes,
        fileCategory,
        validSheetNames
      );

      setUploadComplete(true);

      // Reset the fields and clear any previous error
      setFile(null);
      setIsFileSelected(false);
      setNotes('');
      setFileCategory(null);
      setUploadError(null);
      setSheetNames([]);
    } catch (err) {
      console.error('Error uploading file:', err);

      // Try to extract a more detailed error message
      let errorMessage = 'Failed to upload the file.';
      if (err.response) {
        // If using Axios, the error object may contain a response with data
        errorMessage = err.response.data.message || JSON.stringify(err.response.data);
      } else if (err.message) {
        errorMessage = err.message;
      }

      setUploadError(errorMessage);
      alert(errorMessage);
    }
  };

  return (
    <div style={{ padding: '12px', fontSize: '16px' }}>
      <input type="file" accept=".xlsx, .xls" onChange={handleFileChange} />
      {file && <p style={{ marginTop: '8px' }}>File Selected: {file.name}</p>}

      {file && (
        <div style={{ marginTop: '8px' }}>
          <label htmlFor="notes">Notes: </label>
          <input
            type="text"
            id="notes"
            value={notes}
            onChange={e => setNotes(e.target.value)}
            placeholder="Enter notes for this file"
            style={{ fontSize: '14px', marginLeft: '8px', padding: '4px' }}
          />
        </div>
      )}

      {isFileSelected && (
        <div style={{ marginTop: '12px' }}>
          <p style={{ marginBottom: '4px' }}>Select a Category:</p>
          {Object.values(Category).map(cat => (
            <div key={cat} style={{ marginBottom: '4px' }}>
              <input
                type="radio"
                id={cat}
                name="category"
                value={cat}
                checked={fileCategory === cat}
                onChange={() => handleCategoryChange(cat)}
              />
              <label
                htmlFor={cat}
                style={{
                  marginLeft: '6px',
                  fontSize: '14px',
                }}
              >
                {cat}
              </label>
            </div>
          ))}
        </div>
      )}

      {isFileSelected && fileCategory && (
        <div style={{ marginTop: '12px', display: 'flex', gap: '8px' }}>
          <button
            onClick={handleUpload}
            style={{
              fontSize: '14px',
              padding: '6px 12px',
              backgroundColor: '#4CAF50',
              color: 'white',
              border: 'none',
              cursor: 'pointer',
              borderRadius: '4px',
            }}
          >
            Upload
          </button>

          <button
            onClick={() => {
              setFile(null);
              setNotes('');
              setIsFileSelected(false);
              setFileCategory(null);
              setUploadError(null);
            }}
            style={{
              fontSize: '14px',
              padding: '6px 12px',
              backgroundColor: '#ccc',
              color: '#333',
              border: 'none',
              cursor: 'pointer',
              borderRadius: '4px',
            }}
          >
            Clear
          </button>
        </div>
      )}

      {uploadComplete && (
        <p style={{ color: 'green', marginTop: '10px' }}>
          Upload successful! Please refresh the page to see the uploaded file.
        </p>
      )}
    </div>
  );
};

export default ExcelFileUploader;
